// Last five conversations across the tenant — a quick "is anyone using
// this?" pulse. Self-fetches via TanStack Query; if the user already
// visited /conversations the cache is warm and this paints instantly.
//
// Click on a row routes to /conversations (the inline-expand pattern on
// that page already covers the trace; deep-linking to a specific row
// would require a route change — out of scope for the Home pass).

import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, MessagesSquare } from 'lucide-react';
import type { ConversationSummary } from '@cortex/shared';
import { useApiClient } from '../../lib/providers.js';
import {
  conversationsApi,
  CONVERSATIONS_QUERY_KEY,
} from '../../lib/api/conversations.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card.js';
import { EmptyState } from '../ui/empty-state.js';
import { ErrorRetry } from '../ui/error-retry.js';
import { Skeleton } from '../ui/skeleton.js';
import { Button } from '../ui/button.js';
import { formatUsd } from '../../lib/format-money.js';
import { formatDate } from '../../lib/format-date.js';

const RELATIVE = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });

function relativeTime(iso: string): string {
  const ms = new Date(iso).getTime() - Date.now();
  const abs = Math.abs(ms);
  const minute = 60_000;
  const hour = 60 * minute;
  const day = 24 * hour;
  if (abs < minute) return 'just now';
  if (abs < hour) return RELATIVE.format(Math.round(ms / minute), 'minute');
  if (abs < day) return RELATIVE.format(Math.round(ms / hour), 'hour');
  if (abs < 7 * day) return RELATIVE.format(Math.round(ms / day), 'day');
  return formatDate(iso);
}

export function RecentActivityCard() {
  const api = useApiClient();
  const list = useQuery({
    queryKey: CONVERSATIONS_QUERY_KEY,
    queryFn: () => conversationsApi.list(api),
    // Slice client-side so the Home cache stays compatible with the full
    // /conversations page (same queryKey → no double fetch).
    select: (data) => data.conversations.slice(0, 5),
  });

  return (
    <Card className="h-full">
      <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
        <div className="space-y-1.5">
          <CardTitle>Recent activity</CardTitle>
          <CardDescription>Latest conversations across your agents.</CardDescription>
        </div>
        <Button variant="ghost" size="sm" asChild>
          <Link to="/conversations" aria-label="View all conversations">
            View all
            <ArrowRight className="h-4 w-4" aria-hidden />
          </Link>
        </Button>
      </CardHeader>
      <CardContent className="p-0">
        {list.isLoading && <ActivitySkeleton />}
        {list.isError && (
          <ErrorRetry
            title="Could not load conversations"
            error={list.error}
            onRetry={() => void list.refetch()}
            className="m-4"
          />
        )}
        {list.data?.length === 0 && (
          <EmptyState
            icon={MessagesSquare}
            title="No conversations yet"
            description="Your first chat will appear here within seconds of arriving."
            className="m-4 border-0 bg-transparent py-6"
          />
        )}
        {list.data && list.data.length > 0 && (
          <ul className="divide-y" role="list">
            {list.data.map((row) => (
              <ActivityRow key={row.id} row={row} />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

// Static row — the header's "View all" link is the canonical exit to
// /conversations, and there's no per-row deep-link contract on that page yet,
// so a clickable row would only re-route to the list (a no-op trip). Stays
// informational; promote to a Link if /conversations gains ?selected= later.
function ActivityRow({ row }: { row: ConversationSummary }) {
  return (
    <li className="flex items-center gap-3 px-5 py-3">
      <span
        aria-hidden
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary"
      >
        <MessagesSquare className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1 space-y-0.5">
        <div className="flex items-center justify-between gap-2">
          <p className="truncate text-sm font-medium">
            {row.agentName ?? <span className="italic text-muted-foreground">deleted</span>}
          </p>
          <span className="shrink-0 text-xs text-muted-foreground">
            {relativeTime(row.updatedAt)}
          </span>
        </div>
        <p className="truncate text-xs text-muted-foreground tabular-nums">
          {row.messageCount} messages · {formatUsd(row.totalCostUsd, { precise: true })}
        </p>
      </div>
    </li>
  );
}

function ActivitySkeleton() {
  return (
    <ul className="divide-y">
      {Array.from({ length: 5 }).map((_, i) => (
        <li key={i} className="flex items-center gap-3 px-5 py-3">
          <Skeleton className="h-9 w-9 shrink-0 rounded-md" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-3 w-24" />
          </div>
          <Skeleton className="h-3 w-12" />
        </li>
      ))}
    </ul>
  );
}
